import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import AttractionCarousel from "../components/AttractionCarousel";
import Marquee from "../components/Marquee";
import {
  HERO, MANIFESTO, COLLECTIONS, DESTINATIONS, JOURNEY, HOTELS, WHY,
  TESTIMONIALS, STATS, GALLERY, SERVICES,
} from "../data/content";

const ease = [0.16, 1, 0.3, 1];

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 2, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = 0.75; }, []);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] overflow-hidden bg-navy">
      <motion.div style={{ y }} className="absolute inset-0">
        <video
          ref={videoRef} autoPlay muted loop playsInline poster={HERO.poster}
          className="w-full h-full object-cover"
        >
          <source src={HERO.video} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.62)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,16,56,0.5), transparent 30%, rgba(13,16,56,0.75))" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[12vh]">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.9, duration: 1 }} className="overline text-gold mb-6">
          End to End Destinations · The Private Travel House
        </motion.p>
        <h1 className="font-serif text-ivory text-[12vw] leading-[0.98] md:text-[6.6vw] md:leading-[0.98] max-w-[17ch] pb-[0.08em]">
          <MaskLines lines={["Every journey begins", "with a destination."]} delay={2.9} />
          <span className="block text-gold italic">
            <MaskLines lines={["We make it unforgettable."]} delay={3.2} lineClass="italic" />
          </span>
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.6, duration: 1 }} className="flex flex-wrap gap-4 mt-10">
          <Link to="/prive" data-testid="hero-prive-cta" className="btn-gold">Explore Privé <ArrowRight size={16} /></Link>
          <Link to="/collective" data-testid="hero-collective-cta" className="btn-ghost text-ivory">Explore Collective</Link>
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4, duration: 1 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="overline text-ivory/60 text-[10px]">Scroll</span>
        <span className="h-10 w-[1px] bg-gold/50" />
      </motion.div>
    </section>
  );
}

function Manifesto() {
  return (
    <section id="manifesto" data-testid="manifesto" className="bg-ivory py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="overline text-charcoal/50">Our Philosophy</p>
          <h2 className="font-serif text-4xl md:text-6xl text-navy mt-6 max-w-[20ch] leading-[1.05]">
            Every experience deserves perfection.
          </h2>
          <p className="text-charcoal/70 max-w-xl mt-8 text-lg leading-relaxed">
            We design bespoke journeys that blend luxury, authenticity and effortless comfort — quiet, considered, and entirely your own.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-4 gap-px bg-navy/10 mt-20">
          {MANIFESTO.map((m, idx) => (
            <Reveal key={m.n} delay={idx * 0.08} className="bg-ivory">
              <div className="p-8 md:p-10 h-full">
                <p className="font-serif text-gold text-2xl">{m.n}</p>
                <h3 className="font-serif text-3xl text-navy mt-6">{m.t}</h3>
                <p className="text-charcoal/60 mt-4 leading-relaxed text-[15px]">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gateway() {
  const sides = [
    { name: "Privé", sub: "Bespoke Private Luxury Travel", img: DESTINATIONS[0].hero, to: "/prive", dark: true, testid: "gateway-prive" },
    { name: "Collective", sub: "Group & Institutional Travel Solutions", img: DESTINATIONS[5].hero, to: "/collective", dark: false, testid: "gateway-collective" },
  ];
  return (
    <section data-testid="gateway" className="relative grid md:grid-cols-2 min-h-[92vh]">
      <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gold/50 z-20" />
      {sides.map((s) => (
        <Link key={s.name} to={s.to} data-testid={s.testid} className="group relative h-[70vh] md:h-auto overflow-hidden">
          <img src={s.img} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
          <div className="absolute inset-0" style={{ background: s.dark ? "rgba(13,16,56,0.6)" : "rgba(13,16,56,0.45)" }} />
          <div className="relative h-full flex flex-col justify-center items-center text-center px-8">
            <p className="overline text-gold mb-5">{s.sub}</p>
            <h2 className="font-serif text-6xl md:text-7xl text-ivory">{s.name}</h2>
            <span className="btn-ghost text-ivory mt-10 group-hover:border-gold">Explore <ArrowRight size={16} /></span>
          </div>
        </Link>
      ))}
    </section>
  );
}

function Collections() {
  return (
    <section data-testid="collections" className="bg-ivory py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="overline text-charcoal/50">Luxury Collections</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">Where dreams become destinations.</h2>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {COLLECTIONS.map((c, idx) => (
            <Reveal key={c.name} delay={(idx % 3) * 0.08}>
              <Link to={`/destinations/${c.slug}`} data-testid={`collection-${c.name.toLowerCase().replace(/\s|&/g, "")}`} className="group block relative overflow-hidden h-[440px] grain">
                <img src={c.img} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.85), transparent 60%)" }} />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="font-serif text-4xl text-ivory">{c.name}</h3>
                  <p className="text-ivory/70 mt-3 text-sm leading-relaxed max-w-xs">{c.sub}</p>
                  <span className="inline-flex items-center gap-2 text-gold overline mt-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">Discover <ArrowUpRight size={14} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Destinations() {
  return (
    <section id="destinations" data-testid="destinations" className="bg-navy grain py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="overline text-gold">Signature Destinations</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5 max-w-[16ch]">A gallery of extraordinary places.</h2>
          </div>
          <Link to="/destinations" data-testid="view-catalogue" className="btn-ghost text-ivory shrink-0">View Full Catalogue <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
      <div className="flex gap-6 overflow-x-auto px-6 md:px-10 pb-6 snap-x" style={{ scrollbarWidth: "none" }} data-testid="destinations-scroller">
        {DESTINATIONS.map((d, idx) => (
          <Reveal key={d.slug} delay={(idx % 4) * 0.05} className="shrink-0 snap-start">
            <Link to={`/destinations/${d.slug}`} data-testid={`destination-card-${d.slug}`} className="group block w-[78vw] sm:w-[52vw] md:w-[30vw] lg:w-[24vw]">
              <div className="relative h-[440px] overflow-hidden grain">
                <img src={d.thumb} alt={d.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.5), transparent 55%)" }} />
              </div>
              <div className="pt-5">
                <p className="overline text-gold/80">{d.region}</p>
                <h3 className="font-serif text-3xl text-ivory mt-2 group-hover:text-gold transition-colors">{d.name}</h3>
                <p className="text-ivory/55 mt-2 text-sm leading-relaxed">{d.tag}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function AttractionsSection() {
  return (
    <section data-testid="attractions-section" className="bg-navy grain pb-28 md:pb-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-12">
        <Reveal>
          <p className="overline text-gold">Iconic Moments</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5">The world, in focus.</h2>
        </Reveal>
      </div>
      <AttractionCarousel />
    </section>
  );
}

function Journey() {
  return (
    <section data-testid="journey" className="bg-ivory py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal className="mb-16">
          <p className="overline text-charcoal/50">The Journey</p>
          <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">From dream to memory.</h2>
        </Reveal>
        <div className="grid md:grid-cols-6 gap-y-10 gap-x-6 relative">
          <div className="hidden md:block absolute top-3 left-0 right-0 h-px bg-navy/10" />
          {JOURNEY.map((j, idx) => (
            <Reveal key={j.t} delay={idx * 0.08} className="relative">
              <span className="block h-2 w-2 rounded-full bg-gold mb-6" />
              <p className="font-serif text-gold text-lg">0{idx + 1}</p>
              <h3 className="font-serif text-2xl text-navy mt-2">{j.t}</h3>
              <p className="text-charcoal/60 mt-2 text-sm leading-relaxed">{j.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section data-testid="why" className="bg-navy grain py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal className="mb-16">
          <p className="overline text-gold">Why Travel With Us</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5 max-w-[18ch]">Luxury defined through care, not excess.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-px bg-ivory/10">
          {WHY.map((w, idx) => (
            <Reveal key={w.t} delay={(idx % 3) * 0.06} className="bg-navy">
              <div className="p-8 md:p-10">
                <h3 className="font-serif text-2xl text-gold">{w.t}</h3>
                <p className="text-ivory/60 mt-3 leading-relaxed text-[15px]">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Hotels() {
  return (
    <section data-testid="hotels" className="bg-ivory py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-14">
        <Reveal>
          <p className="overline text-charcoal/50">Handpicked Luxury Collection</p>
          <h2 className="font-serif text-4xl md:text-6xl text-navy mt-5">The world's finest houses.</h2>
        </Reveal>
      </div>
      <div className="border-y border-navy/10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
          {HOTELS.map((h, idx) => (
            <Reveal key={h} delay={(idx % 3) * 0.05}>
              <div className="py-10 md:py-14 border-b border-navy/10 flex items-center justify-center">
                <span className="font-serif text-2xl md:text-3xl text-navy/80 hover:text-gold transition-colors">{h}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="stats" className="bg-navy grain py-24 md:py-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-12 text-center">
        {STATS.map((s, idx) => (
          <Reveal key={s.label} delay={idx * 0.1}>
            <p className="font-serif text-7xl md:text-8xl text-gold"><Counter to={s.n} suffix={s.suffix} /></p>
            <p className="overline text-ivory/70 mt-4">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function VideoInterlude() {
  const videoRef = useRef(null);
  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = 0.7; }, []);
  return (
    <section data-testid="video-interlude" className="relative h-[70vh] overflow-hidden bg-navy">
      <video ref={videoRef} autoPlay muted loop playsInline poster={DESTINATIONS[0].hero} className="absolute inset-0 w-full h-full object-cover">
        <source src={HERO.video2} type="video/mp4" />
      </video>
      <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.55)" }} />
      <div className="relative h-full flex items-center justify-center text-center px-8">
        <Reveal>
          <p className="overline text-gold mb-6">A Glimpse</p>
          <h2 className="font-serif text-ivory text-4xl md:text-7xl max-w-[18ch] leading-[1.05]">
            Enter a world curated specifically for you.
          </h2>
        </Reveal>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="testimonials" className="bg-ivory py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal className="mb-16"><p className="overline text-charcoal/50 text-center">In Their Words</p></Reveal>
        <div className="grid md:grid-cols-3 gap-10">
          {TESTIMONIALS.map((t, idx) => (
            <Reveal key={idx} delay={idx * 0.1}>
              <motion.div animate={{ y: [0, -4, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: idx * 0.6 }}
                className="border border-navy/10 p-10 h-full bg-white/60">
                <p className="text-gold tracking-[0.3em]">★★★★★</p>
                <p className="font-serif text-2xl text-navy mt-6 leading-snug italic">“{t.q}”</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section data-testid="gallery" className="bg-navy grain py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-14">
        <Reveal><p className="overline text-gold">Gallery</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5">Moments, quietly extraordinary.</h2></Reveal>
      </div>
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {GALLERY.map((src, idx) => (
          <Reveal key={idx} delay={(idx % 4) * 0.05} className={idx % 5 === 0 ? "md:row-span-2" : ""}>
            <div className={`overflow-hidden grain group ${idx % 5 === 0 ? "h-full min-h-[300px]" : "h-[240px]"}`}>
              <img src={src} alt="Luxury travel gallery" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" data-testid="services" className="bg-navy py-24 md:py-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Reveal className="mb-16 max-w-2xl">
          <p className="overline text-gold">Travel Services</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5">Every detail, handled.</h2>
          <p className="text-ivory/60 mt-6 leading-relaxed">Beyond the journey itself, we manage the essentials that make travel effortless — from the flight to the paperwork to the currency in your hand.</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.t} delay={idx * 0.08}>
              <div className="group relative h-[440px] overflow-hidden border border-ivory/10">
                <img src={s.img} alt={s.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 transition-all duration-[1200ms] ease-out group-hover:opacity-80 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.94), rgba(13,16,56,0.35) 65%)" }} />
                <div className="relative h-full flex flex-col justify-end p-8">
                  <p className="font-serif text-gold/80 text-lg">{s.n}</p>
                  <h3 className="font-serif text-3xl text-ivory mt-3">{s.t}</h3>
                  <p className="text-ivory/70 mt-4 leading-relaxed text-[15px]">{s.d}</p>
                  <Link to="/enquire" className="inline-flex items-center gap-1 text-gold overline mt-6 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`service-cta-${idx}`}>
                    Enquire <ArrowUpRight size={12} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Consultation() {
  return (
    <section data-testid="consultation" className="relative py-32 md:py-48 overflow-hidden bg-navy">
      <img src={DESTINATIONS[2].hero} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
      <img src={DESTINATIONS[2].hero} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
      <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.7)" }} />
      <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 text-center">
        <Reveal>
          <p className="overline text-gold mb-6">Personal Consultation</p>
          <h2 className="font-serif text-ivory text-5xl md:text-7xl max-w-[20ch] mx-auto leading-[1.05]">
            Let's design your next bespoke journey.
          </h2>
          <div className="flex flex-wrap gap-4 justify-center mt-12">
            <Link to="/enquire" data-testid="consultation-cta" className="btn-gold">Begin Your Journey <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Manifesto />
      <Gateway />
      <Collections />
      <Destinations />
      <AttractionsSection />
      <Marquee items={["Discretion", "Curation", "Craft", "Presence", "Elegance"]} className="bg-navy py-10 border-y border-gold/20" />
      <Journey />
      <Why />
      <Hotels />
      <Services />
      <Stats />
      <VideoInterlude />
      <Testimonials />
      <Gallery />
      <Consultation />
    </main>
  );
}
